import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';

import { Translation, TranslocoService } from '@ngneat/transloco';

import { NotificationService } from '@core/services';

import { Columns } from '#types/grid';

import { AuthService } from '@auth/services';

export interface ExportData {
  columns: Columns;
  format: string;
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  loading$ = new BehaviorSubject<boolean>(false);
  step$ = new BehaviorSubject<string>(null);
  data$ = new BehaviorSubject<ExportData>(null);

  nameid: string;

  translations: Translation = {};
  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  worker: Worker;

  constructor(
    private authService: AuthService,
    private translocoService: TranslocoService,
    private notificationService: NotificationService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe((translations) => {
        this.translations = translations;

        this.authService.nameid$.pipe(take(1)).subscribe((nameid) => {
          this.nameid = nameid;
        });
      });

    this.worker = new Worker(new URL('client/app/core/services/export.service/export.worker.ts', import.meta.url), {
      name: 'export.worker',
      type: 'module',
    });

    this.worker.addEventListener('message', (event) => {
      if (event.data.status === 'OKAY') {
        this.download(event.data.filename, event.data.mimeType, event.data.format, event.data.buffer);
      } else {
        this.displayError(event.data);
      }
    });
  }

  export(columns: Columns, format: string, filename: string, includeHighlights: boolean, data: any) {
    this.step$.next(this.translate('core.components.grid.export.generating_export_file'));

    this.worker.postMessage({
      columns,
      format,
      filename,
      includeHighlights,
      data,
      translations: this.translations,
      nameid: this.nameid,
    });
  }

  displayError(error: any) {
    console.error(error);

    this.notificationService.error({
      title: this.translate('core.components.grid.export.error.title'),
      message: this.translate('core.components.grid.export.error.message'),
    });

    this.loading$.next(false);

    // set these to null after two seconds to allow animations to complete
    setTimeout(() => {
      this.data$.next(null);
      this.step$.next(null);
    }, 2000);
  }

  download(filename: string, mimeType: string, format: string, data: any) {
    let blob: Blob;
    let url: string;

    if (format === 'csv' || format === 'xlsx') {
      blob = new Blob([data], {
        type: mimeType,
      });

      url = window.URL.createObjectURL(blob);
    } else if (format === 'json') {
      url = `data:${mimeType};charset=utf-8,${data as string}`;
    }

    const a = document.createElement('a');
    a.href = url;

    a.download = filename;

    document.body.appendChild(a);
    a.style.display = 'none';
    a.click();
    a.remove();

    setTimeout(() => {
      this.loading$.next(false);
      window.URL.revokeObjectURL(url);
    }, 1000);

    // set these to null after two seconds to allow animations to complete
    setTimeout(() => {
      this.data$.next(null);
      this.step$.next(null);
    }, 2000);
  }
}
